import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { userAPI, notificationAPI } from '../../services/api';

// Async Thunks
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userAPI.getNotifications();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch notifications');
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await userAPI.markNotificationAsRead(notificationId);
      return { notificationId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark notification as read');
    }
  }
);

export const sendPushNotification = createAsyncThunk(
  'notifications/sendPush',
  async (notificationData, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.sendPushNotification(notificationData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send push notification');
    }
  }
);

export const sendSMS = createAsyncThunk(
  'notifications/sendSMS',
  async (smsData, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.sendSMS(smsData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send SMS');
    }
  }
);

export const sendEmail = createAsyncThunk(
  'notifications/sendEmail',
  async (emailData, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.sendEmail(emailData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to send email');
    }
  }
);

export const fetchNotificationTemplates = createAsyncThunk(
  'notifications/fetchTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.getNotificationTemplates();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch notification templates');
    }
  }
);

export const createNotificationTemplate = createAsyncThunk(
  'notifications/createTemplate',
  async (templateData, { rejectWithValue }) => {
    try {
      const response = await notificationAPI.createNotificationTemplate(templateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create notification template');
    }
  }
);

// Initial State
const initialState = {
  notifications: [],
  templates: [],
  unreadCount: 0,
  loading: false,
  templatesLoading: false,
  sendingNotification: false,
  error: null,
  successMessage: null,
  preferences: {
    push: true,
    email: true,
    sms: false,
    orderUpdates: true,
    promotions: true,
    systemAlerts: true,
  },
  filters: {
    type: 'all', // 'all', 'order', 'promotion', 'system', 'support'
    status: 'all', // 'all', 'read', 'unread'
    dateRange: null,
  },
};

// Notification Slice
const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    addNotification: (state, action) => {
      const notification = action.payload;
      state.notifications.unshift(notification);
      if (!notification.read) {
        state.unreadCount += 1;
      }
    },
    removeNotification: (state, action) => {
      const notificationId = action.payload;
      const notificationIndex = state.notifications.findIndex(
        notification => notification._id === notificationId
      );
      if (notificationIndex !== -1) {
        const notification = state.notifications[notificationIndex];
        if (!notification.read) {
          state.unreadCount -= 1;
        }
        state.notifications.splice(notificationIndex, 1);
      }
    },
    markAllAsRead: (state) => {
      state.notifications.forEach(notification => {
        notification.read = true;
      });
      state.unreadCount = 0;
    },
    clearAllNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
    },
    updatePreferences: (state, action) => {
      state.preferences = { ...state.preferences, ...action.payload };
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        type: 'all',
        status: 'all',
        dateRange: null,
      };
    },
    // Real-time notification handling
    receiveNotification: (state, action) => {
      const notification = {
        ...action.payload,
        received: new Date().toISOString(),
        read: false,
      };
      state.notifications.unshift(notification);
      state.unreadCount += 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload.notifications || action.payload;
        state.unreadCount = state.notifications.filter(n => !n.read).length;
        state.error = null;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Mark Notification as Read
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const { notificationId } = action.payload;
        const notification = state.notifications.find(n => n._id === notificationId);
        if (notification && !notification.read) {
          notification.read = true;
          notification.readAt = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      
      // Send Push Notification
      .addCase(sendPushNotification.pending, (state) => {
        state.sendingNotification = true;
        state.error = null;
      })
      .addCase(sendPushNotification.fulfilled, (state) => {
        state.sendingNotification = false;
        state.successMessage = 'Push notification sent successfully!';
        state.error = null;
      })
      .addCase(sendPushNotification.rejected, (state, action) => {
        state.sendingNotification = false;
        state.error = action.payload;
      })
      
      // Send SMS
      .addCase(sendSMS.pending, (state) => {
        state.sendingNotification = true;
        state.error = null;
      })
      .addCase(sendSMS.fulfilled, (state) => {
        state.sendingNotification = false;
        state.successMessage = 'SMS sent successfully!';
        state.error = null;
      })
      .addCase(sendSMS.rejected, (state, action) => {
        state.sendingNotification = false;
        state.error = action.payload;
      })
      
      // Send Email
      .addCase(sendEmail.pending, (state) => {
        state.sendingNotification = true;
        state.error = null;
      })
      .addCase(sendEmail.fulfilled, (state) => {
        state.sendingNotification = false;
        state.successMessage = 'Email sent successfully!';
        state.error = null;
      })
      .addCase(sendEmail.rejected, (state, action) => {
        state.sendingNotification = false;
        state.error = action.payload;
      })
      
      // Fetch Notification Templates
      .addCase(fetchNotificationTemplates.pending, (state) => {
        state.templatesLoading = true;
        state.error = null;
      })
      .addCase(fetchNotificationTemplates.fulfilled, (state, action) => {
        state.templatesLoading = false;
        state.templates = action.payload.templates || action.payload;
        state.error = null;
      })
      .addCase(fetchNotificationTemplates.rejected, (state, action) => {
        state.templatesLoading = false;
        state.error = action.payload;
      })
      
      // Create Notification Template
      .addCase(createNotificationTemplate.fulfilled, (state, action) => {
        state.templates.push(action.payload);
        state.successMessage = 'Notification template created successfully!';
      });
  },
});

export const {
  clearError,
  clearSuccessMessage,
  addNotification,
  removeNotification,
  markAllAsRead,
  clearAllNotifications,
  updatePreferences,
  setFilters,
  clearFilters,
  receiveNotification,
} = notificationSlice.actions;

export default notificationSlice.reducer;